import config from 'config';

export function parseVideoIdInput({
  asset,
  defaultVideoAspectRatio = config.get('public.brand.defaultVideoAspectRatio'),
}) {
  const { customVars } = asset?.options || {};
  const videoId =
    customVars?.videoId || customVars?.VideoID || customVars?.splitVideoId;
  const videoAspectRatio =
    customVars?.videoAspectRatio > 0
      ? customVars.videoAspectRatio
      : defaultVideoAspectRatio;
  const videoTargetUrl = customVars?.videoTargetUrl;
  const videoType = 'vimeo';

  return {
    videoId: isNaN(videoId) ? null : videoId,
    videoAspectRatio,
    videoType,
    videoTargetUrl,
  };
}

/**
 * @const
 * Defined Vimeo custom media player properties per their developer SDK
 * (https://developer.vimeo.com/player/sdk/embed).
 * Includes defined default values if we decide to validate CMS defined customVars.
 */
const VIMEO_PLAYER_OPTIONS_MAP = new Map([
  ['id', null],
  ['autopause', true],
  ['autoplay', false],
  ['background', false],
  ['byline', true],
  ['color', null],
  ['controls', true],
  ['dnt', false],
  ['height', null],
  ['interactive_params', null],
  ['keyboard', true],
  ['loop', false],
  ['maxheight', null],
  ['maxwidth', null],
  ['muted', false],
  ['pip', false],
  ['playsinline', true],
  ['portrait', true],
  ['quality', 'auto'],
  ['responsive', false],
  ['speed', false],
  ['texttrack', null],
  ['title', true],
  ['transparent', true],
  ['width', null],
]);

/**
 * Translates an assets customVars defined in CMS and parses out any non-supported properties that are
 * not approved in the given media players documentation but are defined in CMS. If there are no
 * media player approved customVars defined in CMS, do nothing and return an empty config object.
 *
 * Notes:
 * - The return is targeted for the `Video` component from `reactui` which uses
 * `react-player` (https://www.npmjs.com/package/react-player) under the covers.
 * - Currently, this utility only supports Vimeo videos. For extended media player support, please consult
 * the media players developer documentation and add extended support here.
 *
 * @function
 * @param {Object} asset - single asset defined in CMS
 * @returns {Object} custom config properties and values for supported media players
 */
export function parseMediaPlayerConfigFromAsset({ asset }) {
  const assetCustomVars = asset?.options?.customVars || null;
  const mediaPlayerCustomConfig = {};

  if (assetCustomVars) {
    Object.keys(assetCustomVars).forEach((assetCustomVarsKey) => {
      if (VIMEO_PLAYER_OPTIONS_MAP.has(assetCustomVarsKey)) {
        mediaPlayerCustomConfig[assetCustomVarsKey] =
          assetCustomVars[assetCustomVarsKey];
      }
    });
  }

  return {
    vimeo: {
      playerOptions: mediaPlayerCustomConfig,
    },
  };
}
